import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { ui } from "../../../components/ui";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

function MetricRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="per-file-hint shrink-0">{label}</span>
      <span className="font-semibold text-foreground text-right truncate">{value}</span>
    </div>
  );
}

export function InventoryRecordTable({
  data,
  page,
  pageSize,
  onPageChange,
  totalItems,
  onRowClick,
}) {
  const { t } = useTranslation();

  const resolvedTotalItems = totalItems ?? data.length;
  const totalPages = Math.max(1, Math.ceil(resolvedTotalItems / pageSize));
  const pagedRows = useMemo(() => {
    if (totalItems != null) return data;
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize, totalItems]);

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const dateObj = new Date(isoStr);
    if (!Number.isFinite(dateObj.getTime())) return "—";
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const sourceLabel = (sourceType) => {
    const labels = {
      Purchase_From_Vendor: "inventory.purchaseFromVendor",
      "Purchase from Vendor": "inventory.purchaseFromVendor",
      Purchase: "inventory.purchaseFromVendor",
      Warranty_Replacement_From_Vendor: "inventory.warrantyReplacementFromVendor",
      "OEM Warranty Replacement from Vendor": "inventory.warrantyReplacementFromVendor",
      "OEM Warranty Replacement": "inventory.warrantyReplacementFromVendor",
      Defective_Replacement_From_Vendor: "inventory.defectiveReplacementFromVendor",
      "Defective Replacement from Vendor": "inventory.defectiveReplacementFromVendor",
      Excess_Return_From_Customer: "inventory.excessReturnFromCustomer",
      "Return from Customer": "inventory.excessReturnFromCustomer",
      "Excess Return": "inventory.excessReturnFromCustomer",
      Warranty_Replacement_From_Customer: "inventory.warrantyReplacementFromCustomer",
      Defective_Replacement_From_Customer: "inventory.defectiveReplacementFromCustomer",
    };
    const key = labels[sourceType];
    return key ? t(key) : sourceType || "—";
  };

  return (
    <div className="font-sans">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className={ui.tableHead}>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.reference")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.sourceType")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("dateTime.receiptDate")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.product")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.batchNumber")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.acceptedQuantity")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.warehouseLocation")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.party")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-(--color-text-border)/35">
            {pagedRows.map((row) => (
              <tr
                key={row.id || row.inventoryInId}
                className="hover:bg-workspace/20 transition-colors cursor-pointer"
                onClick={() => onRowClick?.(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onRowClick?.(row);
                  }
                }}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? "button" : undefined}
              >
                <td className="px-4 py-3.5 font-semibold text-foreground font-mono whitespace-nowrap">
                  {row.inventoryInId || "—"}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {sourceLabel(row.sourceType)}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {formatDate(row.receiptDate)}
                </td>
                <td className="px-4 py-3.5">
                  <span className="font-semibold text-foreground block truncate leading-tight">
                    {row.productName || "—"}
                  </span>
                </td>
                <td className="px-4 py-3.5 per-file-hint font-mono truncate">
                  {row.batchNumber || "—"}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {row.acceptedQuantity ?? "—"}
                </td>
                <td className="px-4 py-3.5 per-file-hint truncate">
                  {row.warehouseName || "—"}
                </td>
                <td className="px-4 py-3.5 per-file-hint truncate">
                  {row.partyName ||
                    row.vendorName ||
                    row.customerName ||
                    "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden space-y-2.5 p-3">
        {pagedRows.map((row) => (
          <li
            key={row.id || row.inventoryInId}
            className="rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs cursor-pointer active:bg-workspace/30"
            onClick={() => onRowClick?.(row)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onRowClick?.(row);
              }
            }}
            tabIndex={onRowClick ? 0 : undefined}
            role={onRowClick ? "button" : undefined}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {row.productName || "—"}
              </p>
              <p className="text-xs font-mono per-file-hint mt-0.5">
                {row.inventoryInId || "—"}
              </p>
            </div>
            <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25">
              <MetricRow
                label={t("inventory.sourceType")}
                value={sourceLabel(row.sourceType)}
              />
              <MetricRow
                label={t("dateTime.receiptDate")}
                value={formatDate(row.receiptDate)}
              />
              <MetricRow
                label={t("inventory.batchNumber")}
                value={row.batchNumber || "—"}
              />
              <MetricRow
                label={t("inventory.acceptedQuantity")}
                value={row.acceptedQuantity ?? "—"}
              />
              <MetricRow
                label={t("inventory.warehouseLocation")}
                value={row.warehouseName || "—"}
              />
              <MetricRow
                label={t("inventory.party")}
                value={
                  row.partyName ||
                  row.vendorName ||
                  row.customerName ||
                  "—"
                }
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="px-4 py-3">
        <TablePagination
          page={page}
          totalPages={totalPages}
          totalItems={resolvedTotalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
